import { useState } from 'react';
import { H1 } from 'foundations-components/transitional/components/H1';
import { H2 } from 'foundations-components/transitional/components/H2';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Link } from 'foundations-components/transitional/components/Link';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { Section } from 'foundations-components/transitional/components/Section';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { View } from 'foundations-components/transitional/components/View';
import { getAppNameByAppId, INTEGRATIONS_APPS_DATA } from '../../../../constants/AppIds';
import { AI_APPS_DATA } from './aiAppsData';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUPPORT_LINK = 'https://knowledge.hubspot.com/integrations/create-and-use-custom-llm-workflow-actions';
export const AIReconnectModal = ({
  appId,
  onClose,
  onSuccess,
  onFailure
}) => {
  const [apiKey, setApiKey] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isInteracted, setIsInteracted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const appKey = getAppNameByAppId(appId);
  const {
    displayName: appName
  } = INTEGRATIONS_APPS_DATA[appKey];
  const appData = AI_APPS_DATA[appKey];
  if (!appData) {
    return null;
  }
  const {
    validateApiKey,
    submitApiKey,
    helpLink
  } = appData;
  const validate = value => {
    const isValid = validateApiKey(value);
    setValidationError(!isValid ? I18n.text('reconnect.aiReconnectModal.validationMessages.apiKey', {
      appName
    }) : '');
  };
  const handleChange = e => {
    const newValue = e.target.value;
    setApiKey(newValue);
    setIsInteracted(true);
    validate(newValue);
  };
  const submitForm = () => {
    setIsSubmitting(true);
    void submitApiKey({
      apiKey,
      onSuccess: () => {
        onSuccess === null || onSuccess === void 0 || onSuccess(apiKey);
      },
      onFailure: errorMessage => {
        setValidationError(errorMessage);
        onFailure === null || onFailure === void 0 || onFailure({
          error: new Error(errorMessage),
          errorMessage
        });
      }
    }).finally(() => {
      setIsSubmitting(false);
    });
  };
  const isDisabled = !isInteracted || Boolean(validationError) || isSubmitting;
  return /*#__PURE__*/_jsxs(Modal, {
    width: "848px",
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reconnect.aiReconnectModal.headerTitle",
          options: {
            appName
          }
        })
      })
    }), /*#__PURE__*/_jsx(DialogBody, {
      paddingInlineEnd: "1300",
      paddingInlineStart: "1300",
      children: /*#__PURE__*/_jsxs(Section, {
        marginBlockEnd: "700",
        children: [/*#__PURE__*/_jsx(H1, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reconnect.aiReconnectModal.title",
            options: {
              appName
            }
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "700",
          children: /*#__PURE__*/_jsx(Link, {
            external: true,
            href: helpLink,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reconnect.aiReconnectModal.helpLink"
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "700",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reconnect.aiReconnectModal.description.title",
            options: {
              appName
            }
          })
        }), /*#__PURE__*/_jsxs(List, {
          marginBlockEnd: "700",
          children: [/*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reconnect.aiReconnectModal.description.stepOne",
              options: {
                appName
              }
            })
          }), /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reconnect.aiReconnectModal.description.stepTwo"
            })
          }), /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reconnect.aiReconnectModal.description.stepThree"
            })
          })]
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "700",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "reconnect.aiReconnectModal.description.descriptionLink",
            options: {
              link: /*#__PURE__*/_jsx(Link, {
                onClick: () => {
                  window.open(SUPPORT_LINK, '_blank');
                },
                external: false,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "reconnect.aiReconnectModal.description.link"
                })
              })
            }
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reconnect.aiReconnectModal.label",
            options: {
              appName
            }
          }),
          error: Boolean(validationError),
          validationMessage: validationError && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reconnect.aiReconnectModal.validationMessages.apiKey",
            options: {
              appName
            }
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            value: apiKey,
            onChange: handleChange,
            error: Boolean(validationError)
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      paddingInlineEnd: "1300",
      paddingInlineStart: "1300",
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        onClick: submitForm,
        loading: isSubmitting,
        disabled: isDisabled,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reconnect.aiReconnectModal.buttons.reconnect",
          options: {
            appName
          }
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        size: "medium",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reconnect.aiReconnectModal.buttons.cancel"
        })
      })]
    })]
  });
};